import { useNavigate } from 'react-router';

import { type Permission, type User } from '@models/user';
import { canUserRead } from '@utils/access';

import { useMeStore } from '@features/users/store';

import SecondaryButton from '@components/SecondaryButton';
import s from './ProfilePage.module.css';

type InfoRowProps = {
  label: string;
  value: string | boolean | string[] | undefined | Permission[];
};

const renderArrayValue = (arr: (string | Permission)[] | undefined) => {
  if (!arr || arr.length === 0) return 'None';

  const isPermissionArray = arr.filter((item) => typeof item !== 'string').length > 0;
  if (isPermissionArray) {
    const sanitizedArray = arr.filter((item) => (item as Permission).code !== '*');

    return sanitizedArray.map((item) => (item as Permission).title).join(', ');
  }

  return arr.map((item) => item).join(', ');
};

const InfoRow = ({ label, value }: InfoRowProps) => (
  <div className={s.infoRow}>
    <span className={s.infoLabel}>{label}:</span>
    <span className={s.infoValue}>
      {Array.isArray(value) ? renderArrayValue(value) : String(value)}
    </span>
  </div>
);

const USER_FIELDS: { label: string; key: keyof User }[] = [
  { label: 'ID', key: 'id' },
  { label: 'Name', key: 'name' },
  { label: 'Email', key: 'email' },
  { label: 'Roles', key: 'roles' },
  { label: 'Permissions', key: 'permissions' },
];

function ProfilePage() {
  const meStore = useMeStore();
  const navigate = useNavigate();

  const handleNavigateToEdit = () => {
    navigate(`/dashboard/users/${meStore?.id}/edit`);
  };

  if (!meStore || !meStore.id) {
    return <div>Something went wrong...</div>;
  }

  const readEnabled = canUserRead(meStore);

  return (
    <div className={s.userProfilePage}>
      <div className={s.userHeaderBar}>
        <h1 className={s.userProfileTitle}>User profile</h1>
        <div className={s.userHeaderAction}>
          <SecondaryButton
            title={'Edit User'}
            onClick={handleNavigateToEdit}
            isDisabled={!readEnabled}
          />
        </div>
      </div>
      <div className={s.userProfileBlock}>
        <div className={s.userProfileImageBlock}>
          <img src={meStore?.photoUrl} alt="User Avatar" className={s.userProfileImage} />
        </div>
        <div className={s.userProfileInfoBlock}>
          {USER_FIELDS.map((field) => (
            <InfoRow key={field.key} label={field.label} value={meStore[field.key]} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
